import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { TextInput } from '../../../../form/textInput';
import { selectDataType, operationType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';
import { Main, Styled } from '../easyOrders/style';

type BostaProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

type BostaResource = 'delivery' | 'pickup' | 'address';
type BostaRequest =
   | 'createDelivery'
   | 'updateDelivery'
   | 'getDeliveries'
   | 'getDelivery'
   | 'trackDelivery'
   | 'printAWB'
   | 'getPickupLocations'
   | 'createPickup'
   | 'updatePickup'
   | 'getPickups'
   | 'getPickup'
   | 'getCities'
   | 'getDistricts'
   | 'getZones'
   | 'getAllDistricts';

const BOSTA_EGYPT_COUNTRY_ID = '60e4482c7cb7d4bc4849c4d5';

const resourceOptionsBase: selectDataType[] = [
   { id: 1, name: 'Delivery', refId: 'delivery' },
   { id: 2, name: 'Pickup', refId: 'pickup' },
   { id: 3, name: 'Address', refId: 'address' },
];

const requestOptionsByResource: Record<BostaResource, selectDataType[]> = {
   delivery: [
      { id: 1, name: 'Create Delivery', refId: 'createDelivery' },
      { id: 2, name: 'Update Delivery', refId: 'updateDelivery' },
      { id: 3, name: 'Get Deliveries', refId: 'getDeliveries' },
      { id: 4, name: 'Get Delivery', refId: 'getDelivery' },
      { id: 5, name: 'Track Delivery', refId: 'trackDelivery' },
      { id: 6, name: 'Print AWB', refId: 'printAWB' },
   ],
   pickup: [
      { id: 1, name: 'Get Pickup Locations', refId: 'getPickupLocations' },
      { id: 2, name: 'Create Pickup', refId: 'createPickup' },
      { id: 3, name: 'Update Pickup', refId: 'updatePickup' },
      { id: 4, name: 'Get Pickups', refId: 'getPickups' },
      { id: 5, name: 'Get Pickup', refId: 'getPickup' },
   ],
   address: [
      { id: 1, name: 'Get Cities', refId: 'getCities' },
      { id: 2, name: 'Get Districts', refId: 'getDistricts' },
      { id: 3, name: 'Get Zones', refId: 'getZones' },
      { id: 4, name: 'Get All Districts', refId: 'getAllDistricts' },
   ],
};

const defaultResource: BostaResource = 'delivery';
const defaultRequest = requestOptionsByResource[defaultResource][0].refId as string;

function stringifyTemplate(value: Record<string, any>) {
   return JSON.stringify(value, null, 2);
}

const payloadTemplates: Partial<Record<BostaRequest, string>> = {
   createDelivery: stringifyTemplate({
      type: 10,
      specs: {
         packageType: 'Parcel',
         size: 'MEDIUM',
         packageDetails: {
            itemsCount: 1,
            description: 'Workflow delivery',
         },
      },
      cod: 0,
      dropOffAddress: {
         city: 'Cairo',
         districtId: 'district-id',
         firstLine: 'Street address more than 5 chars',
         secondLine: 'Nearby landmark',
         isWorkAddress: false,
      },
      businessReference: 'ORDER-1001',
      receiver: {
         firstName: 'Customer',
         lastName: 'Name',
         phone: '01000000000',
         email: 'customer@example.com',
      },
      notes: 'Created from Dragify workflow',
   }),
   updateDelivery: stringifyTemplate({
      notes: 'Updated from Dragify workflow',
   }),
   createPickup: stringifyTemplate({
      scheduledDate: 'YYYY-MM-DD',
      businessLocationId: 'location_id',
      contactPerson: {
         name: 'Contact Name',
         phone: '01000000000',
         secPhone: '',
         email: 'contact@example.com',
      },
      notes: 'Pickup from workflow',
      numberOfParcels: 1,
      packageType: 'Normal',
      repeatedData: {
         repeatedType: 'One Time',
         days: [],
         startDate: 'YYYY-MM-DD',
         endDate: 'YYYY-MM-DD',
      },
   }),
   updatePickup: stringifyTemplate({
      notes: 'Updated pickup notes',
   }),
};

const queryTemplates: Partial<Record<BostaRequest, string>> = {
   getDeliveries: stringifyTemplate({
      page: 1,
      limit: 20,
   }),
   getPickups: stringifyTemplate({
      page: 1,
      limit: 20,
   }),
};

function getPayloadTemplate(request?: string) {
   return payloadTemplates[request as BostaRequest] || '';
}

function getQueryTemplate(request?: string) {
   return queryTemplates[request as BostaRequest] || '';
}

function getResourceFromRequest(request?: string): BostaResource {
   if (!request) return defaultResource;
   for (const resource of Object.keys(requestOptionsByResource) as BostaResource[]) {
      if (requestOptionsByResource[resource].some(option => option.refId === request)) {
         return resource;
      }
   }
   return defaultResource;
}

const functionDocs: Record<string, string> = {
   createDelivery: 'Creates a Bosta delivery with raw Bosta delivery payload JSON.',
   updateDelivery: 'Requires Delivery ID and payload JSON object.',
   getDeliveries: 'Lists deliveries. Optional query JSON is sent with apiVersion=1.',
   getDelivery: 'Requires Tracking Number or Delivery ID.',
   trackDelivery: 'Requires Tracking Number.',
   printAWB: 'Requires Delivery ID and returns Bosta AWB response.',
   getPickupLocations: 'Lists business pickup locations for the connected Bosta account.',
   createPickup: 'Creates a Bosta pickup request with raw pickup payload JSON.',
   updatePickup: 'Requires Pickup ID and payload JSON object.',
   getPickups: 'Lists pickup requests. Optional query JSON is supported.',
   getPickup: 'Requires Pickup ID.',
   getCities: `Lists cities for Egypt by default (${BOSTA_EGYPT_COUNTRY_ID}).`,
   getDistricts: 'Requires City ID. Returns districts for that city.',
   getZones: 'Requires City ID. Returns zones for that city.',
   getAllDistricts: `Lists all districts for Egypt by default (${BOSTA_EGYPT_COUNTRY_ID}).`,
};

export function Bosta({ id, data, setChildMinimize, setChildSettings }: BostaProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            resource: defaultResource,
            request: defaultRequest,
            countryId: BOSTA_EGYPT_COUNTRY_ID,
            payload: getPayloadTemplate(defaultRequest),
            query: getQueryTemplate(defaultRequest),
         });
      } else if (!data.value?.resource && data.value?.request) {
         updateNodeValue(id, setNodes, { resource: getResourceFromRequest(data.value.request) });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   };

   const activeRequestValue = data.value?.request as string | undefined;
   const activeResourceValue = (data.value?.resource as BostaResource | undefined) || getResourceFromRequest(activeRequestValue);
   const requestOptionsForResource = requestOptionsByResource[activeResourceValue];
   const activeRequest = requestOptionsForResource.some(request => request.refId === activeRequestValue)
      ? (activeRequestValue as string)
      : (requestOptionsForResource[0]?.refId as string);

   const resourceOptions = resourceOptionsBase.map(option => ({ ...option, isActive: option.refId === activeResourceValue }));
   const actionOptions = requestOptionsForResource.map(option => ({ ...option, isActive: option.refId === activeRequest }));

   const handleSelectResource = (selectId: number) => {
      const nextResource = resourceOptionsBase.find(resource => resource.id === selectId)?.refId as BostaResource | undefined;
      if (!nextResource) return;
      const firstAction = requestOptionsByResource[nextResource][0]?.refId as string | undefined;
      updateNodeValue(id, setNodes, {
         resource: nextResource,
         request: firstAction,
         countryId: data.value?.countryId || BOSTA_EGYPT_COUNTRY_ID,
         payload: getPayloadTemplate(firstAction),
         query: getQueryTemplate(firstAction),
      });
   };

   const handleSelectAction = (selectId: number) => {
      const refId = requestOptionsForResource.find(request => request.id === selectId)?.refId;
      if (!refId) return;
      updateNodeValue(id, setNodes, {
         resource: activeResourceValue,
         request: refId,
         countryId: data.value?.countryId || BOSTA_EGYPT_COUNTRY_ID,
         payload: getPayloadTemplate(refId),
         query: getQueryTemplate(refId),
      });
   };

   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   };

   const isDeliveryIdRequest = ['updateDelivery', 'printAWB'].includes(activeRequest);
   const isDeliveryLookupRequest = activeRequest === 'getDelivery';
   const isTrackingRequest = ['getDelivery', 'trackDelivery'].includes(activeRequest);
   const isPickupIdRequest = ['updatePickup', 'getPickup'].includes(activeRequest);
   const isCityIdRequest = ['getDistricts', 'getZones'].includes(activeRequest);
   const isCountryIdRequest = ['getCities', 'getAllDistricts'].includes(activeRequest);
   const isPayloadRequest = ['createDelivery', 'updateDelivery', 'createPickup', 'updatePickup'].includes(activeRequest);
   const isQueryRequest = ['getDeliveries', 'getPickups'].includes(activeRequest);
   const isAddressRequest = activeResourceValue === 'address';
   const isWarning = !data.value?.connection && !isAddressRequest;

   return (
      <>
         <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
         <Main>
            {isWarning ? (
               <>
                  <div id="warning"></div>
                  <span id="warningIcon"><IoWarningOutline size={21} /></span>
               </>
            ) : data.output ? (
               <>
                  <div id="ran"></div>
                  <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
               </>
            ) : null}

            {isExpanded ? (
               <ExpandedBlock
                  data={{ id, ...data }}
                  handleClose={ () => setIsExpanded(false) }
                  isWarning={ isWarning }
                  style={{ minWidth: '420px' }}
               >
                  <Styled>
                     <div className="action">
                        <AuthenticateSelect
                           type="bosta"
                           defaultId={ data.value?.connection?.id }
                           handleAuthenticate={ handleAuthenticate }
                           background={ data.background }
                        />
                     </div>

                     <Select title="Resource" data={ resourceOptions } handleSelectItem={ handleSelectResource } isSmall={ true } />
                     <Select title="Action" data={ actionOptions } handleSelectItem={ handleSelectAction } isSmall={ true } />

                     {isDeliveryIdRequest || isDeliveryLookupRequest ? (
                        <TextInput keyRef="deliveryId" value={ data.value?.deliveryId } title="Delivery ID" placeholder="delivery-id" handleChange={ handleChangeInput } />
                     ) : null}
                     {isTrackingRequest ? (
                        <TextInput keyRef="trackingNumber" value={ data.value?.trackingNumber } title="Tracking No." placeholder="tracking-number" handleChange={ handleChangeInput } />
                     ) : null}
                     {isPickupIdRequest ? (
                        <TextInput keyRef="pickupId" value={ data.value?.pickupId } title="Pickup ID" placeholder="pickup-id" handleChange={ handleChangeInput } />
                     ) : null}
                     {isCityIdRequest ? (
                        <TextInput keyRef="cityId" value={ data.value?.cityId } title="City ID" placeholder="city-id" handleChange={ handleChangeInput } />
                     ) : null}
                     {isCountryIdRequest ? (
                        <TextInput keyRef="countryId" value={ data.value?.countryId || BOSTA_EGYPT_COUNTRY_ID } title="Country ID" placeholder={ BOSTA_EGYPT_COUNTRY_ID } handleChange={ handleChangeInput } />
                     ) : null}

                     {isPayloadRequest ? (
                        <Textarea
                           title="Payload"
                           keyRef="payload"
                           value={ data.value?.payload }
                           placeholder={ getPayloadTemplate(activeRequest) || '{\n  "field": "value"\n}' }
                           handleChange={ (e) => updateNodeValue(id, setNodes, { payload: e.target.value }) }
                        />
                     ) : null}

                     {isQueryRequest ? (
                        <Textarea
                           title="Query"
                           keyRef="query"
                           value={ data.value?.query }
                           placeholder={ getQueryTemplate(activeRequest) || '{\n  "page": 1\n}' }
                           handleChange={ (e) => updateNodeValue(id, setNodes, { query: e.target.value }) }
                        />
                     ) : null}

                     <div className="previewSection">
                        <h5>Function Block Preview</h5>
                        <pre>{ functionDocs[activeRequest] || 'No function metadata available.' }</pre>
                     </div>
                  </Styled>
               </ExpandedBlock>
            ) : null}
         </Main>
      </>
   );
}
